import { useState, useCallback, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  Search, Loader2, Download,
} from 'lucide-react';
import { getTranslations, useTranslation, formatCount, formatNumber, pluralize, type Language } from '@/i18n';
import { apiClient } from '@/utils/apiClient';
import type { TableData } from '@/utils/apiClient';

/**
 * Collect More is offered on every local page while the API handed back a
 * continuation key — with or without a reported total (STORY-329). Not only
 * on the last page (STORY-456): a list capped at 500 opens on page 1 of 25,
 * and the button that fetches the rest must not hide behind 24 clicks.
 */
export function canCollectMore(continuationKey: string | null): boolean {
  return !!continuationKey;
}

/**
 * The footer caption once the API total is known. `loaded` is every row
 * already in the store (not the page window). A problems list counts
 * problems, with the verb and noun agreeing with the number; any other list
 * (hosts, entities, metrics, …) gets the neutral «Showing N of X».
 */
export function formatLoadedCaption(
  language: Language,
  listKey: string | undefined,
  loaded: number,
  apiTotal: number,
): string {
  const t = getTranslations(language).chat;
  const template = listKey === 'problems'
    ? pluralize(language, loaded, t.problemsLoaded)
    : t.itemsLoaded;
  return template
    .replace('{loaded}', formatNumber(language, loaded))
    .replace('{total}', formatNumber(language, apiTotal));
}

interface DynamicTableProps {
  tableData: TableData;
}

type SortDir = 'asc' | 'desc' | null;

/**
 * Pre-built table component for large API result sets.
 *
 * Receives structured data (columns + rows) from the backend — no LLM
 * formatting needed.  Supports pagination (via /api/paginate/{id}?page=N),
 * client-side sort (per page), and client-side search (per page).
 * When the API has more data than initially loaded, a "Collect More" button
 * appears on every page to fetch the next batch (STORY-456).
 */
export function DynamicTable({ tableData }: DynamicTableProps) {
  const { t, language } = useTranslation();
  const { columns, pagination_id, page_size, list_key } = tableData;

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [collectingMore, setCollectingMore] = useState(false);
  const [pageRows, setPageRows] = useState<Record<number, Record<string, any>[]>>({
    1: tableData.rows,
  });
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Mutable pagination state — updated when "Collect More" fetches new data
  const [totalCount, setTotalCount] = useState(tableData.total_count);
  const [totalPages, setTotalPages] = useState(tableData.total_pages);
  const [continuationKey, setContinuationKey] = useState(tableData.continuation_key || null);
  const [apiTotalCount, setApiTotalCount] = useState<number | null>(tableData.api_total_count ?? null);

  // Fetch a page from the backend paginate endpoint
  const fetchPage = useCallback(async (page: number) => {
    if (pageRows[page]) {
      setCurrentPage(page);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.fetchPage(pagination_id, page);
      const items: Record<string, any>[] = data.items || [];
      setPageRows(prev => ({ ...prev, [page]: items }));
      setCurrentPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.chat.pageFetchFailed);
    } finally {
      setLoading(false);
    }
  }, [pagination_id, pageRows, t]);

  // Collect more data from the API (fetch next batch with continuation key)
  const handleCollectMore = useCallback(async () => {
    setCollectingMore(true);
    setError(null);
    try {
      const result = await apiClient.collectMore(pagination_id);
      // An empty batch carries no counters — nothing more to fetch.
      if (!result.new_items_count) {
        setContinuationKey(null);
        return;
      }
      // Clear cached pages beyond current — new pages have new data
      // Keep existing pages, new ones will be fetched on demand
      setPageRows(prev => {
        const kept: Record<number, Record<string, any>[]> = {};
        for (const [k, v] of Object.entries(prev)) {
          const pageNum = Number(k);
          if (pageNum <= totalPages) kept[pageNum] = v;
        }
        return kept;
      });
      setTotalCount(result.total_count);
      setTotalPages(result.total_pages);
      setContinuationKey(result.continuation_key || null);
      setApiTotalCount(result.api_total_count ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.chat.collectMoreFailed);
    } finally {
      setCollectingMore(false);
    }
  }, [pagination_id, totalPages, t]);

  // Current page rows with sort + search applied
  const displayRows = useMemo(() => {
    let rows = pageRows[currentPage] || [];

    // Client-side search (filter across all visible columns)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      rows = rows.filter(row =>
        columns.some(col => {
          const val = row[col.key];
          if (val == null) return false;
          return String(val).toLowerCase().includes(term);
        })
      );
    }

    // Client-side sort
    if (sortKey && sortDir) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortKey] ?? '';
        const bv = b[sortKey] ?? '';
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortDir === 'asc' ? av - bv : bv - av;
        }
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return rows;
  }, [pageRows, currentPage, searchTerm, sortKey, sortDir, columns]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      // Cycle: asc → desc → null
      setSortDir(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc');
      if (sortDir === 'desc') setSortKey(null);
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const formatCell = (value: any): string => {
    if (value == null) return '—';
    if (typeof value === 'boolean') return value ? '✓' : '✗';
    if (typeof value === 'object') return JSON.stringify(value).slice(0, 80);
    return String(value);
  };

  // Page range for info bar
  const rangeStart = (currentPage - 1) * page_size + 1;
  const rangeEnd = Math.min(currentPage * page_size, totalCount);
  const remaining = apiTotalCount != null ? Math.max(0, apiTotalCount - totalCount) : 0;

  return (
    <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Search bar */}
      <div className="flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        <Search className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder={t.common.search || 'Search this page'}
          className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 outline-none"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="ml-2 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            ✕
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500 mr-2" />
            <span className="text-sm text-gray-500">
              {t.chat.loadingPage.replace('{page}', formatNumber(language, currentPage))}
            </span>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800">
                {columns.map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 cursor-pointer select-none hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1">
                      <span>{col.label}</span>
                      {sortKey === col.key && sortDir === 'asc' && <ChevronUp className="h-3 w-3" />}
                      {sortKey === col.key && sortDir === 'desc' && <ChevronDown className="h-3 w-3" />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {displayRows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-6 text-center text-gray-400">
                    {searchTerm ? t.chat.noMatchingRows : t.chat.noData}
                  </td>
                </tr>
              ) : (
                displayRows.map((row, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
                  >
                    {columns.map(col => (
                      <td
                        key={col.key}
                        className="px-3 py-1.5 text-gray-700 dark:text-gray-300 max-w-xs truncate"
                        title={formatCell(row[col.key])}
                      >
                        {formatCell(row[col.key])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-red-500 px-3 py-2 bg-red-50 dark:bg-red-900/20">
          {error}
        </div>
      )}

      {/* Pagination footer */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => fetchPage(currentPage - 1)}
          disabled={currentPage <= 1 || loading}
          className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t.common.back || 'Prev'}
        </button>

        <div className="flex flex-col items-center text-sm text-gray-600 dark:text-gray-400">
          <span>
            {t.common.page} {formatNumber(language, currentPage)} {t.common.of} {formatNumber(language, totalPages)}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {apiTotalCount != null
              ? formatLoadedCaption(language, list_key, totalCount, apiTotalCount)
              : <>{formatNumber(language, rangeStart)}–{formatNumber(language, rangeEnd)} {t.common.of} {formatCount(language, totalCount, t.common.items, formatNumber(language, totalCount))}</>}
          </span>
        </div>

        <button
          onClick={() => fetchPage(currentPage + 1)}
          disabled={currentPage >= totalPages || loading}
          className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {t.common.next || 'Next'}
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>

      {/* Collect More bar — shown on every page while the API has a continuation key */}
      {canCollectMore(continuationKey) && (
        <div className="flex items-center justify-center px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800">
          <button
            onClick={handleCollectMore}
            disabled={collectingMore || loading}
            className="flex items-center px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {collectingMore ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t.chat.collecting}
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                {remaining > 0
                  ? t.chat.collectMoreRemaining.replace('{remaining}', formatNumber(language, remaining))
                  : t.chat.collectMore}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
